import React, { useEffect, useState } from 'react';
import {
    Box,
    HStack,
    IconButton,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp)$/i;

export function isImageFileName(name = '') {
    return IMAGE_EXT_RE.test(name);
}

export function isImageFile(file) {
    if (!file) return false;
    if (file.type?.startsWith('image/')) return true;
    return isImageFileName(file.name);
}

/**
 * Staged File → object-URL preview (revoked on unmount).
 * Uploaded path → pass previewSrc from storage public URL.
 */
export function FileAttachmentRow({
    fileName,
    file,
    previewSrc,
    onRemove,
    variant = 'staged', // 'staged' | 'uploaded'
    canRemove = true,
}) {
    const [localPreview, setLocalPreview] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const isImage = file ? isImageFile(file) : isImageFileName(fileName);
    const isStaged = variant === 'staged';

    useEffect(() => {
        if (!file || !isImageFile(file)) {
            setLocalPreview(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setLocalPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const imageSrc = localPreview || previewSrc || null;
    const bg = isStaged ? 'white' : 'green.50';
    const borderColor = isStaged ? 'gray.100' : 'green.200';
    const nameColor = isStaged ? 'gray.800' : 'green.700';

    return (
        <>
            <HStack
                justify="space-between"
                align="center"
                bg={bg}
                p={2}
                borderRadius="md"
                boxShadow={isStaged ? 'xs' : 'none'}
                border="1px solid"
                borderColor={borderColor}
                spacing={3}
            >
                <HStack spacing={3} flex={1} minW={0} align="center">
                    {isImage && imageSrc ? (
                        <Box
                            as="button"
                            type="button"
                            onClick={onOpen}
                            flexShrink={0}
                            borderRadius="md"
                            overflow="hidden"
                            border="1px solid"
                            borderColor="gray.200"
                            bg="gray.50"
                            w="72px"
                            h="72px"
                            p={0}
                            cursor="pointer"
                            title="Enlarge preview"
                            _hover={{ opacity: 0.9, boxShadow: 'sm' }}
                            _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400' }}
                        >
                            <Image
                                src={imageSrc}
                                alt={fileName || 'Preview'}
                                w="100%"
                                h="100%"
                                objectFit="cover"
                                fallback={
                                    <Box
                                        w="100%"
                                        h="100%"
                                        bg="gray.100"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Text fontSize="xs" color="gray.500">
                                            Image
                                        </Text>
                                    </Box>
                                }
                            />
                        </Box>
                    ) : (
                        <Box
                            flexShrink={0}
                            w="40px"
                            h="40px"
                            borderRadius="md"
                            bg="gray.100"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="lg"
                            aria-hidden
                        >
                            📄
                        </Box>
                    )}
                    <VStack align="start" spacing={0} minW={0} flex={1}>
                        <Text fontSize="xs" fontWeight="medium" color={nameColor} isTruncated w="100%">
                            {fileName}
                        </Text>
                        {isImage && imageSrc && (
                            <Text fontSize="10px" color="gray.500">
                                Tap preview to enlarge
                            </Text>
                        )}
                    </VStack>
                </HStack>
                {canRemove && onRemove && (
                    <IconButton
                        size="xs"
                        icon={<Text fontWeight="bold">×</Text>}
                        colorScheme="red"
                        variant="ghost"
                        aria-label={`Remove ${fileName}`}
                        onClick={onRemove}
                    />
                )}
            </HStack>

            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay bg="blackAlpha.700" />
                <ModalContent mx={4} bg="white">
                    <ModalHeader fontSize="sm" pr={12} isTruncated>
                        {fileName || 'Image preview'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg="gray.50"
                            borderRadius="md"
                            overflow="hidden"
                            minH="200px"
                        >
                            {imageSrc && (
                                <Image
                                    src={imageSrc}
                                    alt={fileName || 'Preview'}
                                    maxH="70vh"
                                    maxW="100%"
                                    objectFit="contain"
                                />
                            )}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default FileAttachmentRow;
